import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  closeMonth,
  fetchAdminTenants,
  fetchBillingSettings,
  fetchBroadcastEstimate,
  fetchMonthStatements,
  fetchRateCard,
  fetchWhatsappBillingStatus,
  fetchWhatsappUsage,
  patchTenantBilling,
  saveDefaultMargin,
  fetchTenantUsage,
  addTenantWalletCredit,
  fetchTenantWallet,
  patchTenantWallet,
  setTenantSeats,
  setTenantStatus,
} from "@/api/whatsappBilling";
import type {
  AdminBillingSettings,
  AdminTenantsResponse,
  BroadcastEstimateApiResponse,
  CloseMonthResult,
  MonthStatementsResponse,
  TenantBillingPatch,
  TenantBillingResult,
  TenantStatus,
  TenantWallet,
  TenantWalletPatch,
  WhatsappBillingStatusResponse,
  WhatsappUsageResponse,
  AdminTenantUsage,
} from "@/api/whatsappBillingTypes";
import { isBillingMonth } from "@/lib/whatsappCost/billingMonth";
import type { RateCard } from "@/lib/whatsappCost/types";

export const WHATSAPP_BILLING_KEYS = {
  all: ["whatsappBilling"] as const,
  // `accountId` keeps a platform admin's read of ONE tenant apart from their own workspace's.
  rateCard: (accountId?: string) => [...WHATSAPP_BILLING_KEYS.all, "rateCard", accountId ?? "own"] as const,
  usage: (month?: string, accountId?: string) =>
    [...WHATSAPP_BILLING_KEYS.all, "usage", month ?? "current", accountId ?? "own"] as const,
  status: (accountId?: string) => [...WHATSAPP_BILLING_KEYS.all, "status", accountId ?? "own"] as const,
  adminTenantWallet: (workspaceId: string) =>
    [...WHATSAPP_BILLING_KEYS.all, "adminTenantWallet", workspaceId] as const,
  estimate: (templateId?: string | number, segmentId?: string | number | null, total?: number) =>
    [...WHATSAPP_BILLING_KEYS.all, "estimate", templateId ?? "none", segmentId ?? "all", total ?? 0] as const,
  adminTenants: () => [...WHATSAPP_BILLING_KEYS.all, "adminTenants"] as const,
  adminBillingSettings: () => [...WHATSAPP_BILLING_KEYS.all, "adminBillingSettings"] as const,
  /** Prefix of every month's statements, for invalidating them all at once. */
  adminStatementsAll: () => [...WHATSAPP_BILLING_KEYS.all, "adminStatements"] as const,
  adminTenantUsage: (workspaceId: string, month: string) =>
    [...WHATSAPP_BILLING_KEYS.all, "adminTenantUsage", workspaceId, month] as const,
  adminStatements: (month: string) => [...WHATSAPP_BILLING_KEYS.adminStatementsAll(), month] as const,
};

/**
 * Hook to retrieve the active Meta WhatsApp rate card.
 * Cached for 1 hour per repository performance rules.
 */
export function useRateCardQuery(accountId?: string) {
  return useQuery<RateCard>({
    queryKey: WHATSAPP_BILLING_KEYS.rateCard(accountId),
    queryFn: () => fetchRateCard(accountId),
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * One month of WhatsApp usage, priced for this tenant. `month` is "YYYY-MM"; omitted means the
 * tenant's current month. Cached for 60 seconds.
 */
export function useWhatsappUsageQuery(month?: string, accountId?: string) {
  return useQuery<WhatsappUsageResponse>({
    queryKey: WHATSAPP_BILLING_KEYS.usage(month, accountId),
    queryFn: () => fetchWhatsappUsage(month, accountId),
    staleTime: 60 * 1000, // 60 seconds
  });
}

/**
 * Hook to retrieve the tenant's WhatsApp billing configuration (timezone, ad-window length).
 * Cached for 5 minutes.
 */
export function useWhatsappBillingStatusQuery(accountId?: string) {
  return useQuery<WhatsappBillingStatusResponse>({
    queryKey: WHATSAPP_BILLING_KEYS.status(accountId),
    queryFn: () => fetchWhatsappBillingStatus(accountId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to query audience delivery estimate for WhatsApp broadcast.
 */
export function useBroadcastEstimateQuery(params: {
  templateId?: number | string | undefined;
  category?: string | undefined;
  segmentId?: number | null | undefined;
  totalRecipients?: number | undefined;
  enabled?: boolean | undefined;
}) {
  return useQuery<BroadcastEstimateApiResponse>({
    queryKey: WHATSAPP_BILLING_KEYS.estimate(params.templateId, params.segmentId, params.totalRecipients),
    queryFn: () =>
      fetchBroadcastEstimate({
        templateId: params.templateId,
        category: params.category,
        segmentId: params.segmentId,
        totalRecipients: params.totalRecipients,
      }),
    enabled: params.enabled ?? (params.totalRecipients !== undefined && params.totalRecipients > 0),
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook to fetch Platform Admin tenants billing metrics.
 */
export function useAdminTenantsQuery(enabled = true) {
  return useQuery<AdminTenantsResponse>({
    queryKey: WHATSAPP_BILLING_KEYS.adminTenants(),
    queryFn: fetchAdminTenants,
    enabled,
    staleTime: 60 * 1000,
  });
}

/**
 * One tenant's month, with Meta's own figures beside ours.
 *
 * `staleTime` is deliberately short and there is no refetch on focus: the Meta half is a live
 * Graph call, so every extra fetch spends a tenant's rate limit to redraw a number that moves
 * once a day.
 */
export function useAdminTenantUsageQuery(workspaceId: string | null, month: string) {
  return useQuery<AdminTenantUsage>({
    queryKey: WHATSAPP_BILLING_KEYS.adminTenantUsage(workspaceId ?? "none", month),
    queryFn: () => fetchTenantUsage(workspaceId as string, month),
    enabled: Boolean(workspaceId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/** The platform default margin (platform admins only). */
export function useAdminBillingSettingsQuery() {
  return useQuery<AdminBillingSettings>({
    queryKey: WHATSAPP_BILLING_KEYS.adminBillingSettings(),
    queryFn: fetchBillingSettings,
    staleTime: 60 * 1000,
  });
}

/**
 * A margin change reprices every tenant still on it and every month not yet closed, so the
 * tenants table and the live statements are refetched rather than left showing the old price.
 */
function useInvalidateMarginViews() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: WHATSAPP_BILLING_KEYS.adminTenants() });
    void queryClient.invalidateQueries({ queryKey: WHATSAPP_BILLING_KEYS.adminStatementsAll() });
  };
}

/** Sets the platform default margin; null goes back to the deploy's configured value. */
export function useSaveDefaultMargin() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateMarginViews();
  return useMutation<AdminBillingSettings, Error, number | null>({
    mutationFn: saveDefaultMargin,
    onSuccess: (settings) => {
      queryClient.setQueryData(WHATSAPP_BILLING_KEYS.adminBillingSettings(), settings);
      invalidate();
    },
  });
}

/** One tenant's margin override and/or who pays Meta. Only the fields passed are changed. */
export function useSetTenantBilling() {
  const invalidate = useInvalidateMarginViews();
  return useMutation<TenantBillingResult, Error, { workspaceId: string } & TenantBillingPatch>({
    mutationFn: ({ workspaceId, ...patch }) => patchTenantBilling(workspaceId, patch),
    onSuccess: invalidate,
  });
}

/** Every tenant's statement for one "YYYY-MM" month. Asks for nothing until the month is valid. */
export function useMonthStatementsQuery(month: string) {
  return useQuery<MonthStatementsResponse>({
    queryKey: WHATSAPP_BILLING_KEYS.adminStatements(month),
    queryFn: () => fetchMonthStatements(month),
    enabled: isBillingMonth(month),
    staleTime: 60 * 1000,
  });
}

/** One tenant's wallet. Admin only; asks for nothing until a tenant is named. */
export function useTenantWalletQuery(workspaceId: string | null) {
  return useQuery<TenantWallet>({
    queryKey: WHATSAPP_BILLING_KEYS.adminTenantWallet(workspaceId ?? "none"),
    queryFn: () => fetchTenantWallet(workspaceId as string),
    enabled: Boolean(workspaceId),
    staleTime: 30 * 1000,
  });
}

/** After a wallet change: the wallet itself, and the tenants list whose "paused" badge it moves. */
function useWalletSaved(workspaceId: string) {
  const queryClient = useQueryClient();
  return (wallet: TenantWallet) => {
    queryClient.setQueryData(WHATSAPP_BILLING_KEYS.adminTenantWallet(workspaceId), wallet);
    void queryClient.invalidateQueries({ queryKey: WHATSAPP_BILLING_KEYS.adminTenants() });
  };
}

export function useUpdateTenantWallet(workspaceId: string) {
  const saved = useWalletSaved(workspaceId);
  return useMutation<TenantWallet, Error, TenantWalletPatch>({
    mutationFn: (patch) => patchTenantWallet(workspaceId, patch),
    onSuccess: saved,
  });
}

export function useAddWalletCredit(workspaceId: string) {
  const saved = useWalletSaved(workspaceId);
  return useMutation<TenantWallet, Error, { amount: number; note?: string | undefined }>({
    mutationFn: (credit) => addTenantWalletCredit(workspaceId, credit),
    onSuccess: saved,
  });
}

/** A tenant's seat limit; null puts it back on the platform's ceiling. */
export function useSetTenantSeats(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, number | null>({
    mutationFn: (seatLimit) => setTenantSeats(workspaceId, seatLimit),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: WHATSAPP_BILLING_KEYS.adminTenants() }),
  });
}

/** The lifecycle switch: active, automations paused, or suspended. */
export function useSetTenantStatus(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { status: TenantStatus; reason?: string }>({
    mutationFn: ({ status, reason }) => setTenantStatus(workspaceId, status, reason),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: WHATSAPP_BILLING_KEYS.adminTenants() }),
  });
}

/** Locks a finished month's statements, then refetches that month. */
export function useCloseMonth() {
  const queryClient = useQueryClient();
  return useMutation<CloseMonthResult, Error, string>({
    mutationFn: closeMonth,
    onSuccess: (_result, month) => {
      void queryClient.invalidateQueries({ queryKey: WHATSAPP_BILLING_KEYS.adminStatements(month) });
    },
  });
}
